import {
  Dispatch,
  SetStateAction,
  createContext,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useQuery } from '@apollo/client';
import { PaypalTypesEnum } from '@monofront/business/constants';
import {
  useCheckoutCartState,
  useCheckoutComMachine,
} from '@monofront/business/contexts';
import { LIST_PAYMENT_METHODS } from '@monofront/business/queries';
import {
  IListPaymentMethodsQueryResult,
  IPaymentMethod,
  PaymentMethod,
  PaymentMethodSource,
  PaymentMethodTypesEnum,
  PaymentMethodsType,
} from '@monofront/types';
import { useAuthentication } from '@context/businessContainer';
import { usePaymentMethods } from '@features/PaymentMethods/PaymentMethodContext';
import useHandlePaymentError from '../hooks/useHandlePaymentErrors';
import {
  CHECKOUT_COM_CARD_FORM_STATES,
  CHECKOUTCOM_CARD_FORM_TRANSITIONS,
} from '@monofront/business/stateMachines';

interface ISelectedExistingPaymentMethod {
  existingPaymentMethodId: string;
  paymentMethodCode: string;
  paymentMethodSource: PaymentMethodSource;
}

interface ISelectedNewCard {
  isReady: boolean;
  shouldSaveCardForFutureUse: boolean;
  paymentMethodCode: string;
}

interface ISelectedNewPaypal {
  paypalType: PaypalTypesEnum;
  paymentMethodCode: string;
}

interface ISelectedNewApplePay {
  paymentMethodCode: string;
  shouldSaveCardForFutureUse: boolean;
}

type SelectedNewSepa = {
  paymentMethodCode: string;
};

interface ICheckoutPaymentContext {
  isPaymentPageReady: boolean;
  isFlowInitializing: boolean;
  markFlowInitializing: () => void;
  isOrderValidationInProgress: boolean;
  newPaymentOptions: IPaymentMethod[];
  savedPaymentMethods: PaymentMethod[];
  selectedPaymentRadioId: string | null;
  selectedPaymentType: PaymentMethodTypesEnum | null;
  selectedExistingPaymentMethod: ISelectedExistingPaymentMethod | null;
  selectedNewCheckoutComCard: ISelectedNewCard | null;
  selectedNewPaypal: ISelectedNewPaypal | null;
  selectedNewApplePay: ISelectedNewApplePay | null;
  selectedNewSepa: SelectedNewSepa | null;
  setIsOrderValidationInProgress: Dispatch<SetStateAction<boolean>>;
  selectExistingPaymentMethod: (
    paymentRadioId: string,
    paymentMethod: ISelectedExistingPaymentMethod,
    paymentType: PaymentMethodTypesEnum,
  ) => void;
  selectNewCheckoutComOption: (
    paymentRadioId: string,
    paymentType: PaymentMethodTypesEnum,
  ) => void;
  selectNewPaypalOption: (
    paymentRadioId: string,
    paypalMethod: ISelectedNewPaypal,
    paymentType: PaymentMethodTypesEnum,
  ) => void;
  selectNewApplePayOption: (
    paymentRadioId: string,
    paymentType: PaymentMethodTypesEnum,
  ) => void;
  selectNewSepaOption: (
    paymentRadioId: string,
    paymentType: PaymentMethodTypesEnum,
  ) => void;
  updateNewCheckoutComCard: Dispatch<SetStateAction<ISelectedNewCard | null>>;
  clearSelectedPaymentMethod: () => void;
  markFlowReady: () => void;
}

/**
 * This Context returns the state of the Checkout payment page.
 * @returns foo (string): data
 */
const CheckoutPaymentContext = createContext<ICheckoutPaymentContext>({
  isPaymentPageReady: false,
  isFlowInitializing: false,
  markFlowInitializing: () => undefined,
  isOrderValidationInProgress: false,
  newPaymentOptions: [],
  savedPaymentMethods: [],
  selectedPaymentRadioId: null,
  selectedPaymentType: null,
  selectedExistingPaymentMethod: null,
  selectedNewCheckoutComCard: null,
  selectedNewPaypal: null,
  selectedNewApplePay: null,
  selectedNewSepa: null,
  setIsOrderValidationInProgress: () => undefined,
  selectExistingPaymentMethod: () => undefined,
  selectNewCheckoutComOption: () => undefined,
  updateNewCheckoutComCard: () => undefined,
  selectNewPaypalOption: () => undefined,
  selectNewApplePayOption: () => undefined,
  selectNewSepaOption: () => undefined,
  clearSelectedPaymentMethod: () => undefined,
  markFlowReady: () => undefined,
});

/**
 * This Provider handles the Checkout payment page.
 */
const CheckoutPaymentProvider = ({ children }) => {
  const { checkoutCart } = useCheckoutCartState();
  const { state, send } = useCheckoutComMachine();
  const { isLoading } = useAuthentication();
  const { handlePaymentError } = useHandlePaymentError();
  const [selectedPaymentRadioId, setSelectedPaymentRadioId] = useState<
    string | null
  >(null);
  const [selectedPaymentType, setSelectedPaymentType] =
    useState<PaymentMethodTypesEnum | null>(null);
  const [selectedExistingPaymentMethod, setSelectedExistingPaymentMethod] =
    useState<ISelectedExistingPaymentMethod | null>(null);
  const [selectedNewCheckoutComCard, setSelectedNewCheckoutComCard] =
    useState<ISelectedNewCard | null>(null);
  const [selectedNewPaypal, setSelectedNewPaypal] =
    useState<ISelectedNewPaypal | null>(null);
  const [selectedNewApplePay, setSelectedNewApplePay] =
    useState<ISelectedNewApplePay | null>(null);
  const [isOrderValidationInProgress, setIsOrderValidationInProgress] =
    useState(false);
  const [isFlowInitializing, setIsFlowInitializing] = useState(false);

  const { paymentMethods, isLoading: getExistingPaymentMethodsLoading } =
    usePaymentMethods();
  const {
    data: getNewPaymentOptionsQuery,
    loading: getNewPaymentOptionsLoading,
  } = useQuery<IListPaymentMethodsQueryResult>(LIST_PAYMENT_METHODS, {
    ssr: false,
    fetchPolicy: 'cache-and-network',
    variables: {
      tokenValue: checkoutCart?.id,
      paymentId: checkoutCart?.payment?.id,
    },
    skip: !checkoutCart?.id || !checkoutCart?.payment?.id,
    onError: (error) => {
      handlePaymentError({
        code: 'CANNOT_FETCH_NEW_PM',
        message: 'Cannot fetch listPaymentMethods',
        error,
      });
    },
  });

  const isPaymentPageReady =
    !getNewPaymentOptionsLoading &&
    !getExistingPaymentMethodsLoading &&
    !isLoading;

  const savedPaymentMethods = paymentMethods.filter(
    ({ paymentType, source }) =>
      paymentType !== PaymentMethodsType.BankAccount &&
      source !== PaymentMethodSource.STRIPE,
  );
  const newPaymentOptions = useMemo(
    () => getNewPaymentOptionsQuery?.listPaymentMethods || [],
    [getNewPaymentOptionsQuery?.listPaymentMethods],
  );

  const resetSelected = () => {
    setSelectedPaymentType(null);
    setSelectedNewCheckoutComCard(null);
    setSelectedNewPaypal(null);
    setSelectedExistingPaymentMethod(null);

    if (state.value === CHECKOUT_COM_CARD_FORM_STATES['ERROR']) {
      send({ type: CHECKOUTCOM_CARD_FORM_TRANSITIONS.RESET });

      return;
    }
  };

  const selectExistingPaymentMethod = (
    radioId: string,
    paymentMethod: ISelectedExistingPaymentMethod,
    paymentType: PaymentMethodTypesEnum,
  ) => {
    resetSelected();
    setSelectedPaymentRadioId(radioId);
    setSelectedPaymentType(paymentType);
    setSelectedExistingPaymentMethod(paymentMethod);
  };

  const selectNewCheckoutComOption = (
    radioId: string,
    paymentType: PaymentMethodTypesEnum,
  ) => {
    resetSelected();
    setSelectedPaymentRadioId(radioId);
    setSelectedPaymentType(paymentType);
    setSelectedNewCheckoutComCard({
      paymentMethodCode: radioId,
      isReady: false,
      shouldSaveCardForFutureUse: false,
    });
  };

  const selectNewPaypalOption = (
    radioId: string,
    paypalMethod: ISelectedNewPaypal,
    paymentType: PaymentMethodTypesEnum,
  ) => {
    resetSelected();
    setSelectedPaymentRadioId(radioId);
    setSelectedPaymentType(paymentType);
    setSelectedNewPaypal(paypalMethod);
  };

  const selectNewApplePayOption = (
    radioId: string,
    paymentType: PaymentMethodTypesEnum,
  ) => {
    resetSelected();
    setSelectedPaymentRadioId(radioId);
    setSelectedPaymentType(paymentType);
    setSelectedNewApplePay({
      paymentMethodCode: radioId,
      shouldSaveCardForFutureUse: false,
    });
  };

  // Clears the selected payment radio + method state, e.g. when the customer
  // picks a stored card inside the Checkout.com widget.
  const clearSelectedPaymentMethod = () => {
    resetSelected();
    setSelectedPaymentRadioId(null);
  };

  return (
    <CheckoutPaymentContext.Provider
      value={{
        isPaymentPageReady,
        isOrderValidationInProgress,
        savedPaymentMethods,
        newPaymentOptions,
        selectedPaymentRadioId,
        selectedPaymentType,
        selectedExistingPaymentMethod,
        selectedNewCheckoutComCard,
        selectedNewPaypal,
        selectedNewApplePay,
        selectedNewSepa: null,
        selectExistingPaymentMethod,
        selectNewCheckoutComOption,
        updateNewCheckoutComCard: setSelectedNewCheckoutComCard,
        selectNewPaypalOption,
        selectNewApplePayOption,
        selectNewSepaOption: () => undefined,
        clearSelectedPaymentMethod,
        setIsOrderValidationInProgress,
        isFlowInitializing,
        markFlowInitializing: () => setIsFlowInitializing(true),
        markFlowReady: () => setIsFlowInitializing(false),
      }}
    >
      {children}
    </CheckoutPaymentContext.Provider>
  );
};

/**
 * This hook returns the Context of the Checkout payment page.
 * @returns foo (string): data
 */
const useCheckoutPayment = () => useContext(CheckoutPaymentContext);

export { CheckoutPaymentProvider, CheckoutPaymentContext, useCheckoutPayment };
